import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Heart, Wind, Activity, Sparkles, Droplet, User, RotateCcw, Home, TrendingUp } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { useFacialScan } from '../../contexts/FacialScanContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScanCompletePage() {
  const navigate = useNavigate();
  const { scanResults, resetScan } = useFacialScan();

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    // Only redirect if there are truly no scan results and not in saving state
    // Auth is handled by ProtectedRoute in App.js
    if (!scanResults && !isSaving) {
      navigate('/scan/start');
    }
  }, [scanResults, navigate, isSaving]);

  if (!scanResults) return null;

  const metricConfig = {
    heart_rate: { icon: Heart, label: 'Heart Rate', unit: 'bpm', color: '#D54B48' },
    breathing_rate: { icon: Wind, label: 'Breathing', unit: 'rpm', color: '#8BB89F' },
    hrv_sdnn: { icon: Activity, label: 'HRV', unit: 'ms', color: '#F8B64C' },
    stress_index: { icon: Sparkles, label: 'Stress', unit: '%', color: '#C10891' },
    spo2_estimate: { icon: Droplet, label: 'SpO₂', unit: '%', color: '#6D2A83' },
    facial_skin_age: { icon: User, label: 'Skin Age', unit: 'yrs', color: '#8BB89F' }
  };

  const getConfidenceLabel = (confidence) => {
    if (confidence >= 0.85) return { label: 'High', color: '#8BB89F' };
    if (confidence >= 0.70) return { label: 'Medium', color: '#F8B64C' };
    return { label: 'Low', color: '#D54B48' };
  };

  const handleSaveToTwin = async () => {
    try {
      setIsSaving(true);
      // Use the new facial scan sync endpoint with proper validation schema
      await axios.post(`${API}/twin/sync/facial-scan`, {
        scan_type: 'facial_rPPG',
        timestamp: scanResults.timestamp,
        metrics: {
          heart_rate: scanResults.metrics.heart_rate,
          breathing_rate: scanResults.metrics.breathing_rate,
          hrv_sdnn: scanResults.metrics.hrv_sdnn,
          facial_skin_age: scanResults.metrics.facial_skin_age,
          stress_index: scanResults.metrics.stress_index,
          spo2_estimate: scanResults.metrics.spo2_estimate
        }
      });
      toast.success('Vitals saved! Twin Score will refresh soon.');
      // Navigate first, then reset to prevent redirect loop
      navigate('/dashboard');
      resetScan();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save results');
      setIsSaving(false);
    }
  };

  const handleRetry = () => {
    resetScan();
    navigate('/scan/start');
  };

  const handleViewTwin = () => {
    resetScan();
    navigate('/trends');
  };

  const overallConfidence = getConfidenceLabel(
    scanResults.scan_confidence === 'high' ? 0.9 : 0.75
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Success Header */}
      <div className="bg-gradient-to-b from-[#8BB89F]/20 to-transparent pt-12 pb-8 px-6">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', duration: 0.6 }}
          className="flex justify-center mb-4"
        >
          <div className="p-4 rounded-full bg-[#8BB89F]/20 border-2 border-[#8BB89F]">
            <Check size={40} className="text-[#8BB89F]" />
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-center"
        >
          <h1 className="font-heading font-bold text-2xl mb-2">Scan Complete</h1>
          <p className="text-muted-foreground">Your vitals have been captured</p>
        </motion.div>
      </div>

      <main className="px-4 pb-8 -mt-2">
        {/* Confidence Indicator */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="glass-card p-4 mb-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Scan Confidence</span>
              <div className="flex items-center gap-2">
                <span 
                  className="font-semibold"
                  style={{ color: overallConfidence.color }}
                >
                  {overallConfidence.label}
                </span>
                <div 
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: overallConfidence.color }}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {scanResults.scan_confidence === 'high' 
                ? 'Excellent lighting and stable position'
                : 'Indoor lighting OK - try brighter light for better accuracy'}
            </p>
          </Card>
        </motion.div>

        {/* Results Grid */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          {Object.entries(scanResults.metrics).map(([key, data], index) => {
            const config = metricConfig[key];
            if (!config) return null;
            const Icon = config.icon;
            const confidence = getConfidenceLabel(data.confidence);
            
            return (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
              >
                <Card className="glass-card p-4 h-full" data-testid={`result-${key}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <div 
                      className="p-2 rounded-xl"
                      style={{ backgroundColor: `${config.color}20` }}
                    >
                      <Icon size={16} style={{ color: config.color }} />
                    </div>
                    <span className="text-xs text-muted-foreground">{config.label}</span>
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span 
                      className="text-2xl font-bold font-heading"
                      style={{ color: config.color }}
                    >
                      {data.value}
                    </span>
                    <span className="text-xs text-muted-foreground">{config.unit}</span>
                  </div>
                  <div className="flex items-center gap-1 mt-2">
                    <div 
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: confidence.color }}
                    />
                    <span className="text-[10px] text-muted-foreground">
                      {Math.round(data.confidence * 100)}% confidence
                    </span>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="space-y-3"
        >
          <Button
            onClick={handleSaveToTwin}
            className="w-full bg-[#8BB89F] hover:bg-[#8BB89F]/90 rounded-full py-6 text-lg"
            data-testid="save-to-twin-btn"
          >
            Save to Twin
          </Button>
          
          <div className="flex gap-3">
            <Button
              onClick={handleRetry}
              variant="outline"
              className="flex-1 rounded-full"
              data-testid="retry-scan-btn"
            >
              <RotateCcw size={16} className="mr-2" />
              Retry
            </Button>
            <Button
              onClick={handleViewTwin}
              variant="outline"
              className="flex-1 rounded-full"
              data-testid="view-twin-btn"
            >
              <TrendingUp size={16} className="mr-2" />
              View Twin
            </Button>
          </div>
        </motion.div>

        {/* Timestamp */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          Captured {new Date(scanResults.timestamp).toLocaleTimeString()}
        </p>
      </main>
    </div>
  );
}
